import { useState, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { authFetch } from 'api/auth/authFetch'
import { setEpisodeListFetchStatusAction, setProgrammeFetchResultsAction, setIsNewProgrammeAction, setAudiovisualRefreshAction } from 'components/Body/data-entry/AudioVisual/store/actions'
import { selectEpisodesByProgrammeFetchRequest, selectIsNewProgramme } from 'components/Body/data-entry/AudioVisual/store/selectors'

const fetchEpisodes = async (programmeId, page, count, filter, sort, url, controller) => {
    const queryParams = new URLSearchParams({
        pageNumber: page.toString(),
        count: count.toString(),
        ...(filter && { filter: filter.toString() }),
        ...(sort && { sortBy: sort.toString() })
    }).toString()

    const endpoint = `${url}/GetByProgrammeId/${programmeId}?${queryParams}`
    let responseCode = -1

    try {
        const response = await authFetch(endpoint, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal
        })

        if (controller.signal?.aborted) return null

        responseCode = response.status
        if (responseCode === 200) return await response.json()

        throw {
            Message: 'Episode list fetch failed',
            StatusCode: responseCode,
            Details: await response.text()
        }
    } catch (error) {
        throw {
            Message: 'Episode list fetch failed',
            StatusCode: responseCode,
            Details: error
        }
    }
}

// ReSharper disable once InconsistentNaming
const EpisodesByProgrammeApi = ({ isEpisodeForm = false }) => {
    const [abortController, setAbortController] = useState(null)
    const [prevParams, setPrevParams] = useState({ programmeId: null, page: -1, count: -1, sort: null, filter: null })
    const hasProcessedRefresh = useRef(false)

    const dispatch = useDispatch()
    const setStatus = (s) => dispatch(setEpisodeListFetchStatusAction(s))

    const apiRequest = useSelector(selectEpisodesByProgrammeFetchRequest)
    const { programmeId, page, count, filter, sort, url, status, refresh } = apiRequest
    const isNewProgramme = useSelector(selectIsNewProgramme)

    if (!programmeId || isNewProgramme) return undefined

    const isRefresh = refresh === true

    // Reset ref when refresh cycle ends
    if (!isRefresh) {
        hasProcessedRefresh.current = false
    }

    const paramsUnchanged =
        prevParams.programmeId === programmeId &&
        prevParams.page === page &&
        prevParams.count === count &&
        prevParams.filter === filter &&
        prevParams.sort === sort

    if (paramsUnchanged && !isRefresh) return undefined

    // Skip if already initiated fetch for this refresh cycle
    if (paramsUnchanged && isRefresh && hasProcessedRefresh.current) return undefined

    // Only reset refresh if NOT on episode details view
    // For episode details, let EpisodeDetailsHeader reset it after episode fetch
    if (isRefresh && !isEpisodeForm) dispatch(setAudiovisualRefreshAction(false))

    // Mark that we've processed this refresh
    if (isRefresh) {
        hasProcessedRefresh.current = true
    }

    setPrevParams({ programmeId, page, count, filter, sort })

    if (status?.status === 'pending' && abortController) abortController.abort()

    setStatus({ status: 'pending' })
    const controller = new AbortController()
    setAbortController(controller)

    fetchEpisodes(programmeId, page, count, filter, sort, url, controller)
        .then(result => {
            if (result) {
                // Convert episodes to object keyed by episodeId (required by mergeChanges)
                const episodesAsObject = (result.episodes ?? []).reduce((acc, ep) => {
                    acc[ep.episodeId] = { ...ep, id: ep.episodeId }
                    return acc
                }, {})
                const normalizedResult = {
                    ...result,
                    episodes: episodesAsObject
                }
                dispatch(setProgrammeFetchResultsAction(normalizedResult))
                dispatch(setIsNewProgrammeAction(false))
                setStatus({ status: 'complete' })
            }
        })
        .catch(error => {
            setStatus({ status: 'error', error })
        })

    return undefined
}

export default EpisodesByProgrammeApi
